import VectorSource from 'ol/source/Vector';

export function getLegendInfoItem(layer)
{
    return {
        id: layer.get("id"),
        titleEnglish: layer.get("titleEnglish"),
        titleNepali: layer.get("titleNepali"),
        visible: false,
        legendPath: layer.get("legendPath"),
        imgWidth: layer.get("legendImgWidth") || 25,
        imgHeight: layer.get("legendImgHeight") || 25,
        sourceInfo: layer.get("sourceInfo"),
        description: layer.get("description"),
        sourceInfoNepali: layer.get("sourceInfoNepali"),
        descriptionNepali: layer.get("descriptionNepali"),
    };
}

export function filterPossibleLayersForLegends(map)
{
    return map.getLayers().getArray().filter(layer =>
    {
        const source = layer.getSource();
        if (source.constructor===VectorSource && source.getFeatures().length===0)
            return false;
        if (!layer.getVisible() || !layer.get("legendPath"))
            return false;
        return true;
    });
}

export function getPrintLayout()
{
    const DPI = 120; // pdf's dpi
    const mmToPixel = dim_mm => dim_mm * DPI / 25.4; // 1 inch = 25.4 mm
    // all measurements are in mm if not suffixed by pxl
    const margin = 10;
    const topMargin = 15;
    const pageWidth = 297;
    const pageHeight = 210;
    const mapWidth = pageWidth - 2*margin; // 272
    const mapHeight = pageHeight - topMargin - margin; // 180
    const mapHeightPxl = mmToPixel(mapHeight);
    // legend positions are relative to mapframe
    const legendHeightPercent = 0.42;
    const legendHeightPxl = mapHeightPxl * legendHeightPercent;
    const legendPosPxl = {
        x: mmToPixel(2),
        y: mapHeightPxl * (1-legendHeightPercent) - mmToPixel(2)
    }
    // northArrow Coords: also relative to mapFrame
    const arrowBaseWidth = 9;
    const arrowHeight = 12;
    const arrowTop = 6;
    const arrowCenterX = mapWidth - (6 + arrowBaseWidth/2);
    const northArrowCoords = [
        [arrowCenterX, arrowTop], // top coordinate
        [arrowCenterX + arrowBaseWidth/2, arrowTop + arrowHeight], // rightCoordinate
        [arrowCenterX, arrowTop + 2*arrowHeight/3], // middleCoordinate
        [arrowCenterX - arrowBaseWidth/2, arrowTop + arrowHeight], // leftCoordinate
    ];
    return {
        format: "a4",
        pageDim: [pageWidth, pageHeight],
        margin,
        topMargin,
        mapFrameSize: [mapWidth, mapHeight],
        mapFrameSizePxl: [mmToPixel(mapWidth), mapHeightPxl],
        northArrowCoordsPxl: northArrowCoords.map(pt => pt.map(mmToPixel) ),
        legendBoxPxl: {
            pos: legendPosPxl,
            height: legendHeightPxl,
            columnWidth: 230
        },
    };
}

function getLegendBoxDimension(margin, labelHeight, columnWidth, legendInfos, maxBoxHeight)
{
    let bottomMostLegendItemY = 0;
    const lastLegendItemPos = legendInfos.reduce((prevSize, legend) =>
    {
        const itemHeight = legend.imgHeight + labelHeight + margin;
        if (prevSize.y + itemHeight  > maxBoxHeight)
        {
            prevSize.x += margin + columnWidth;
            prevSize.y = margin;
        }
        prevSize.y += itemHeight;
        if (bottomMostLegendItemY < prevSize.y) bottomMostLegendItemY = prevSize.y;
        return prevSize;
    }, {x: margin, y: margin});
    const width = lastLegendItemPos.x + columnWidth + margin;
    const height = bottomMostLegendItemY;
    return [width, height];
}

export function copyOLMapToContext( context )
{
    context.fillStyle = "white";
    context.fillRect(0, 0, context.canvas.width, context.canvas.height);
    document.querySelectorAll(".ol-layer canvas").forEach( olCanvas =>
    {
        if (olCanvas.width <= 0) return; // discard unnecessary canvas
        const opacity = olCanvas.parentNode.style.opacity;
        context.globalAlpha = opacity === "" ? 1 : Number(opacity);
        // get the map's transform parameters from style's transform matrix
        const matrix = (olCanvas.style.transform).match(/^matrix\((.*)\)$/)[1].split(",").map(Number);
        // apply the transform to the exporting temp canvas's context & copy/draw the image from olCanvas to temp context
        context.setTransform(...matrix);
        context.drawImage(olCanvas, 0, 0);
    });
    // reseting context to original transformation
    context.setTransform(1, 0, 0, 1, 0, 0);
}

const loadImage = ( src, {cors} ) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        if (cors) img.crossOrigin = "Anonymous";
        img.src = src;
    });
}

export const addLegendsToContext = async (context, {legendInfos, pos, columnWidth, height: maxHeight}) =>
{
    const margin = 10;
    let offsetX = margin;
    let offsetY = margin;
    const labelHeight = 22;
    const [legendBoxWidth, legendBoxHeight] = getLegendBoxDimension(margin, labelHeight, columnWidth, legendInfos, maxHeight);
    // re-adjust pos y according to legend Box Height
    pos.y += maxHeight - legendBoxHeight;
    // write legend title
    context.textBaseline = "bottom";
    context.fillStyle = "black";
    context.font = "bold 25px Times"
    context.fillText("Legends", pos.x + margin, pos.y - margin);
    // create white canvas on legendbox
    context.fillStyle = "white";
    context.fillRect(pos.x, pos.y, legendBoxWidth, legendBoxHeight);
    // configure text style
    context.fillStyle = "black";
    context.font = "100 18px Times";
    for (let legend of legendInfos)
    {
        const itemHeight = legend.imgHeight + labelHeight + margin;
        if (offsetY + itemHeight > maxHeight)
        {
            offsetX += margin + columnWidth;
            offsetY = margin;
        }
        const left = pos.x + offsetX;
        const top = pos.y + offsetY;
        const img = await loadImage(legend.legendPath, {cors: true});
        context.drawImage(img, left, top + labelHeight, legend.imgWidth, legend.imgHeight)
        context.fillText(legend.titleEnglish, left, top + labelHeight);
 
        offsetY += itemHeight;
    }
    context.lineWidth = 1;
    context.closePath(); // sometimes linewidth doesn't apply unless closePath is called
    context.strokeRect(pos.x, pos.y, legendBoxWidth, legendBoxHeight);
};

export function drawPolygon(context, color, coords)
{
    context.lineWidth = 2;
    context.fillStyle = color;
    context.beginPath();
    context.moveTo(...coords[0]);
    for (let i = 1; i < coords.length; i++)
    {
        context.lineTo(...coords[i]);
    }
    context.closePath();
    context.fill();
}

function getScaleBarInfo()
{
    const scaleLine = document.querySelector(".ol-scale-line-inner");
    return {width: scaleLine.clientWidth, text: scaleLine.innerText};
}

export function drawScaleBar(context, rightBottomPos)
{
    const {width, text} = getScaleBarInfo();
    const height = 30;
    const margin = 8;
    const rightOffset = 15;
    const bottomOffset = 15;
    const pos = {
        x: rightBottomPos.x - (width + margin * 2) - rightOffset,
        y: rightBottomPos.y - (height + margin * 2) - bottomOffset
    };
    // draw background
    context.globalAlpha = 0.75;
    context.fillStyle = "grey";
    context.fillRect(pos.x, pos.y, width+margin*2, height + margin * 2 )
    context.globalAlpha = 1;
    context.lineWidth = 1.5;
    context.closePath()
    context.strokeStyle = "white";
    // draw the scale line shape
    const x = pos.x + margin;
    const y = pos.y + margin;
    context.beginPath()
    context.moveTo(x, y);
    context.lineTo(x, y + height)
    context.lineTo(x + width, y + height)
    context.lineTo(x + width, y);
    context.stroke()
    // write length text below the legend box
    context.textBaseline = "bottom";
    context.textAlign = "center"
    context.fillStyle = "white";
    context.font = "15px Times";
    context.fillText(text, x + width/2, y + height - margin);
}